import { useCallback, useState } from 'react'
import { useLocation, useRoute } from 'wouter'
import { ChevronLeft, Calendar, Clock, MapPin, Loader2, Share2, DollarSign } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Button } from '@/components/ui/button'
import ConfirmModal from '@/components/common/ConfirmationModal'
import useSWR from 'swr'
import { apiFetch } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { getImageUrls } from '@/utils/imageUrls'
import GalleryGrid, { GalleryImage } from '@/components/GalleryGrid'
import defaultImage from '@/assets/default-image.svg'
import { LazyLoadImage } from 'react-lazy-load-image-component'

interface EventDetail {
  id: number | string
  heading: string
  page_title?: string
  page_description?: string
  content?: string
  date_start?: string
  date_end?: string
  event_dates?: string
  location?: string
  price_text?: string
  filename?: string
  filename_alt?: string
  page_url?: string
  published_date?: string
  category_id?: number | null
  gallery?: GalleryImage[]
}

const LoadingState = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 text-[#EF4444] animate-spin" />
      <p className="text-[#BABABB] text-sm mt-4">
        Loading event details...
      </p>
    </div>
  )
}


const ErrorState = ({ message }: { message?: string }) => {
  const [, navigate] = useLocation()

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6">
      <p className="text-[#EF4444] text-center mb-4">
        {message || 'Failed to load event. Please try again later.'}
      </p>
      <Button
        onClick={() => navigate('/events')}
        className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
      >
        Back to Events
      </Button>
    </div>
  )
}

export default function ShowPage() {
  const [, navigate] = useLocation()
  const [match, params] = useRoute('/events/:id')
  const { toast } = useToast()

  const eventId = params?.id

  const { data, error, isLoading } = useSWR(
    eventId ? `/v1/secrets_hideaway/events/${eventId}` : null,
    (url: string) => apiFetch(url, { withCredentials: true }).then((res) => res.json())
  )

  const event: EventDetail | null = data?.success && data?.details?.items?.[0] ? data.details.items[0] : null
  const imageUrls = event?.filename ? getImageUrls('events', event.filename) : null

  const formattedDate = event?.date_start
    ? new Date(event.date_start).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    : event?.event_dates

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.heading || 'Event',
          url
        })
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url)
      toast({
        title: 'Link copied',
        description: 'Event link copied to clipboard'
      })
    }
  }

  const [externalUrl, setExternalUrl] = useState<string | null>(null)

  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest('a')
    if (!anchor) return
    e.preventDefault()
    const href = anchor.getAttribute('href')
    if (href) setExternalUrl(href)
  }, [])

  return (
    <ErrorBoundary>
      <div className="min-h-auto bg-[#18181B] flex flex-col h-full">
        <div className="flex-1 max-w-5xl mx-auto w-full px-5 pb-8">
          {/* Header with back button */}
          <div
            role="banner"
            className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-between text-white py-3 mb-[12px]"
          >
            <div className="flex items-center justify-start gap-2">
              <button
                type="button"
                aria-label="Go back"
                onClick={() => navigate('/events')}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
              </button>
              <h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
                Event Details
              </h1>
            </div>
            {event && (
              <button
                type="button"
                aria-label="Share event"
                onClick={handleShare}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
              >
                <Share2 className="h-[18px] w-[18px] text-[#ffffff]" />
              </button>
            )}
          </div>

          {/* Content */}
          {isLoading ? (
            <LoadingState />
          ) : error || !event ? (
            <ErrorState />
          ) : (
            <>
            {/* Event Image */}
            {imageUrls && (
              <div className="w-full overflow-hidden rounded-xl min-h-[350px]">
                <LazyLoadImage
                  src={imageUrls.source}
                  alt={event.filename_alt || event.heading}
                  effect="opacity"
                  wrapperClassName="lazy-image-wrapper"
                  className="
                    w-full h-full object-cover
                    transition-transform duration-300
                    group-hover:scale-105
                  "
                />
              </div>
            )}

            <h2 className="text-[#ffffff] font-semibold text-[16px] leading-4 font-figtree mb-2">
              {event.heading}
            </h2>

            {/* Event Meta Card */}
            <div className="bg-[#2A2A2A] overflow-hidden mb-4 px-[10px] py-[12px] rounded-[7px]">
              {/* Date */}
              {formattedDate && (
                <div className="flex items-center gap-2 font-figtree text-[13px] leading-4 font-normal">
                  <Calendar className=" text-[#ffffff]" size={22} />
                  <p className="text-[#ffffff] text-[13px] leading-4 font-normal font-figtree">
                    Date: {formattedDate}
                  </p>
                </div>
              )}

              {/* Divider */}
              {formattedDate && event.location && (
                <div className="h-px bg-[#555555] my-3" />
              )}

              {/* Location */}
              {event.location && (
                <div className="flex items-center gap-2 font-figtree text-[13px] leading-4 font-normal">
                  <MapPin className="text-[#ffffff]" size={22} />
                  <p className="text-[#ffffff] text-sm">
                    {event.location}
                  </p>
                </div>
              )}

              {/* Divider */}
              {event.location && event.price_text && (
                <div className="h-px bg-[#4A4A4A] my-3" />
              )}

              {/* Price */}
              {event.price_text && (
                <div className="flex items-center gap-2 justify-between font-figtree leading-4">
                  <div className="flex items-center gap-2 text-[13px] font-normal">
                    <DollarSign className="text-[#ffffff]" size={22} />
                    <p className="text-[#ffffff] text-sm">
                      Entry fee
                    </p>
                  </div>

                  <p className="text-[#ffffff] text-[16px] font-semibold leading-4 font-figtree">
                    {event.price_text}
                  </p>
                </div>
              )}
            </div>


            {/* Description */}
            {event.page_description && (
              <div className="mb-4 font-figtree">
                <h3 className="text-[#ffffff] font-figtree font-medium text-[16px] leading-[16px] mb-2">About</h3>
                <p className="text-[#BABABB] leading-relaxed whitespace-pre-wrap text-[13px] font-normal">
                  {event.page_description}
                </p>
              </div>
            )}

            {/* Full Content */}
            {event.content && (
              <div className="prose prose-invert max-w-none" onClick={handleContentClick}>
                <div
                  className="text-[#BABABB] leading-relaxed [&>p]:mb-3 font-figtree text-[13px] font-normal mb-8 [&_a]:text-[#EF4444] [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: event.content }}
                />
              </div>
            )}

            {/* Gallery Grid */}
            {event.gallery && event.gallery.length > 0 && (
              <GalleryGrid images={event.gallery} title="Gallery" />
            )}
            </>
          )}
        </div>
      </div>
      <ConfirmModal
        open={!!externalUrl}
        onClose={() => setExternalUrl(null)}
        onConfirm={() => {
          if (externalUrl) {
            window.open(externalUrl, '_blank', 'noopener,noreferrer')
          }
          setExternalUrl(null)
        }}
        title="You're leaving the app"
        description="You will be redirected to an external website. The external site's terms and policies will apply."
        confirmText="Continue"
        cancelText="Stay in SECH"
      />
    </ErrorBoundary>
  )
}
